/* eslint-disable restrict-imports/restrict-imports */
import React, { useState } from 'react';
import _ from 'lodash';
import { InputAdornment, OutlinedInput } from '@mui/material';
import { makeStyles } from 'design-system';
import cx from 'clsx';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { ChatMockProps, validateChats } from './utils';
import ChatGPTButton from './components/ChatGPTButton';
import { Sidebar, Upload, Edit, Copy, ThumbsUp, ThumbsDown, Send, Redo } from './components/icons';

const ChatGPTDesktop: React.FC<ChatMockProps> = ({ attachment }: ChatMockProps) => {
  const isAttachmentValid = validateChats(attachment.attachment_body);
  const chats = attachment.attachment_body;
  const classes = useStyles();
  const [darkMode, setDarkMode] = useState<boolean>(!!attachment.darkMode);
  const streamResponses = attachment.streamResponses ?? false;
  const [currentTurns, setCurrentTurns] = useState<number[]>(
    attachment.attachment_body.map(() => 0),
  );
  const [chatStreams, setChatStreams] = useState<any[]>(attachment.attachment_body.map(() => []));
  const [streaming, setStreaming] = useState<boolean>(false);

  const handleStream = async (i: number) => {
    if (chatStreams[i].length < chats[i].length && !streaming) {
      setStreaming(true);
      // Add the turn prompt
      setChatStreams(chatStreams => {
        const newChatStreams = _.cloneDeep(chatStreams);
        newChatStreams[i] = [
          ...chatStreams[i],
          ...chats[i].slice(currentTurns[i], currentTurns[i] + 1),
          { ...chats[i][currentTurns[i] + 1], text: '' },
        ];
        return newChatStreams;
      });
      // Mock stream the turn response
      let textToStream = chats[i][currentTurns[i] + 1].text;
      while (textToStream.length > 0) {
        await new Promise(resolve => {
          setTimeout(() => {
            const skip = Math.round(Math.random() * 40) + 1;
            let nextIndex = textToStream.substring(skip).indexOf(' ') + skip;
            if (nextIndex <= 0) nextIndex = 24;
            setChatStreams(chatStreams => {
              const newChatStreams = _.cloneDeep(chatStreams);
              newChatStreams[i][chatStreams[i].length - 1].text += textToStream.slice(0, nextIndex);
              return newChatStreams;
            });
            textToStream = textToStream.slice(nextIndex);
            resolve();
          }, Math.random() * 200);
        });
      }
      // Set up the next turn
      setCurrentTurns(turns => {
        const newTurns = _.cloneDeep(turns);
        newTurns[i] = turns[i] + 2;
        return newTurns;
      });
      setStreaming(false);
    }
  };

  return (
    <div className={classes.container}>
      {isAttachmentValid ? (
        <>
          {(streamResponses ? chatStreams : attachment.attachment_body).map(
            (chat: any, i: number) => (
              <div
                key={`chat-${i}`}
                className={cx(classes.singleChat, { [classes.singleChatDark]: darkMode })}
                style={{ width: attachment.width ?? 960, height: attachment.height ?? 720 }}
              >
                <header className={cx(classes.header, { [classes.headerDark]: darkMode })}>
                  <ChatGPTButton
                    customIcon={<Sidebar />}
                    secondary
                    darkMode={darkMode}
                    className={classes.headerButton}
                  />
                  <p className={cx(classes.headerText, { [classes.headerTextDark]: darkMode })}>
                    Default
                  </p>
                  <div className={classes.headerRight}>
                    <ChatGPTButton
                      customIcon={<Upload />}
                      darkMode={darkMode}
                      onClick={() => setDarkMode(b => !b)}
                      secondary
                      className={classes.headerButton}
                    />
                  </div>
                </header>
                <div className={classes.body}>
                  {chat.map((turn: any, j: number) => (
                    <ChatTurn turn={turn} darkMode={darkMode} key={`chat-${i}-turn-${j}`} />
                  ))}
                  <footer className={cx(classes.footer, { [classes.footerDark]: darkMode })}>
                    <div className={classes.footerChat}>
                      <div
                        style={{
                          width: 640,
                          display: 'flex',
                        }}
                      >
                        <div style={{ marginLeft: 'auto', marginTop: 24 }}>
                          <ChatGPTButton
                            iconPosition="left"
                            customIcon={<Redo />}
                            darkMode={darkMode}
                          >
                            Regenerate
                          </ChatGPTButton>
                        </div>
                      </div>
                      <OutlinedInput
                        type="text"
                        disabled
                        value={streamResponses ? chats[i][currentTurns[i]]?.text ?? '' : undefined}
                        endAdornment={
                          <InputAdornment position="end">
                            <ChatGPTButton
                              customIcon={<Send />}
                              secondary
                              darkMode={darkMode}
                              disabled={
                                streamResponses
                                  ? streaming || currentTurns[i] >= chats[i].length
                                  : false
                              }
                              onClick={streamResponses ? () => handleStream(i) : () => {}}
                            />
                          </InputAdornment>
                        }
                        placeholder="Send a message"
                        className={cx(classes.chatInput, { [classes.chatInputDark]: darkMode })}
                      />
                    </div>
                    <p
                      className={cx(classes.privacyNotice, {
                        [classes.privacyNoticeDark]: darkMode,
                      })}
                    >
                      This model can make mistakes. Verify important information.
                    </p>
                  </footer>
                </div>
              </div>
            ),
          )}
        </>
      ) : (
        <div>Invalid Bulba Chat Attachment</div>
      )}
    </div>
  );
};

const ChatTurn: React.FC<{ turn: any; darkMode: boolean }> = ({ turn, darkMode }) => {
  const classes = useStyles();
  const isModel = turn.role !== 'user';
  const [hovering, setHovering] = useState<boolean>(false);

  return (
    <div
      className={cx(classes.chatWrapper, {
        [classes.userTurnLight]: !isModel && !darkMode,
        [classes.userTurnDark]: !isModel && darkMode,
        [classes.modelTurnLight]: isModel && !darkMode,
        [classes.modelTurnDark]: isModel && darkMode,
      })}
      onMouseEnter={() => {
        if (!hovering) setHovering(true);
      }}
      onMouseLeave={() => {
        if (hovering) setHovering(false);
      }}
    >
      <div className={cx(classes.chatTurn)}>
        <TurnIcon isModel={isModel} />
        <p
          className={cx(classes.chatText, {
            [classes.chatTextDark]: darkMode,
          })}
        >
          <SanitizedMarkdown
            text={turn.text}
            innerStyle={`line-height: 1.5rem; font-size: 1rem; white-space: pre-wrap; word-break: break-word; margin: 0; ${
              darkMode ? 'font-weight: 400;' : ''
            }`}
          />
        </p>
        <div className={classes.turnIcons}>
          {isModel ? (
            <div className={classes.iconRow}>
              <ChatGPTButton customIcon={<Copy />} secondary darkMode={darkMode} />
              <ChatGPTButton customIcon={<ThumbsUp />} secondary darkMode={darkMode} />
              <ChatGPTButton customIcon={<ThumbsDown />} secondary darkMode={darkMode} />
            </div>
          ) : (
            <>{hovering && <ChatGPTButton customIcon={<Edit />} darkMode={darkMode} secondary />}</>
          )}
        </div>
      </div>
    </div>
  );
};

const TurnIcon: React.FC<{ isModel: boolean }> = ({ isModel }) => {
  const classes = useStyles();

  return (
    <div className={cx(classes.turnIcon, { [classes.turnIconModel]: isModel })}>
      {isModel ? 'M' : 'U'}
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    gap: 16,
    flexWrap: 'wrap',
    margin: '16px 0',
  },
  singleChat: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: 'white',
  },
  singleChatDark: {
    backgroundColor: 'rgb(52, 53, 65)',
  },
  header: {
    height: 56,
    padding: '0.5rem',
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  },
  headerDark: {
    backgroundColor: 'rgb(52, 53, 65)',
    borderBottom: '1px solid rgba(32,33,35,.5)',
  },
  headerText: {
    fontFamily: 'Helvetica Neue,sans-serif',
    fontSize: '0.875rem',
    fontWeight: 400,
    color: 'rgb(96, 99, 103)',
  },
  headerTextDark: {
    color: 'rgb(232, 234, 236)',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
  },
  headerButton: {
    padding: 12,
    height: 44,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    minHeight: 'calc(100% - 56px)',
  },
  iconRow: {
    display: 'flex',
    flexWrap: 'nowrap',
  },
  turnIcons: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 84,
    display: 'flex',
    justifyContent: 'flex-start',
  },
  chatWrapper: {
    display: 'flex',
    flexDirection: 'row',
    padding: '1.5rem 1rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  chatTurn: {
    width: '48rem',
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'start',
    gap: '1.5rem',
    position: 'relative',
  },
  userTurn: {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'start',
    margin: '16px 0 16px 12px',
  },
  userTurnIconMargin: {
    margin: 4,
  },
  chatText: {
    fontFamily: 'Helvetica Neue,sans-serif',
    fontSize: '1rem',
    lineHeight: '1.4rem',
    letterSpacing: 'normal',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    margin: 0,
    display: 'flex',
    justifyContent: 'space-between',
    width: 'calc(100% - 144px)',
    color: 'rgb(55, 65, 81)',
  },
  chatTextDark: {
    color: 'rgb(227, 227, 227)',
  },
  userTurnLight: {
    backgroundColor: 'white',
  },
  userTurnDark: {
    backgroundColor: 'rgb(52, 53, 65)',
  },
  modelTurnLight: {
    backgroundColor: 'rgb(247, 247, 247)',
  },
  modelTurnDark: {
    backgroundColor: 'rgb(68, 70, 83)',
  },
  turnIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: 4,
    backgroundColor: 'rgb(120, 89, 188)',
    color: 'white',
    fontSize: '1.2rem',
  },
  turnIconModel: {
    backgroundColor: 'rgb(91, 192, 131)',
  },
  footer: {
    bottom: 0,
    position: 'sticky',
    width: '100%',
    alignSelf: 'center',
    backgroundColor: 'transparent !important',
    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0) 13.94%, rgb(255, 255, 255) 54.73%)',
    marginTop: 'auto',
  },
  footerDark: {
    backgroundImage: 'linear-gradient(180deg,rgba(53,55,64,0),#353740 58.85%);',
  },
  footerChat: {
    display: 'flex',
    flexDirection: 'column',
    width: 'calc(100% - 16px)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  chatInput: {
    marginTop: 16,
    width: 640,
    borderRadius: '8px !important',
    fontFamily: 'Helvetica Neue,sans-serif !important',
    background: 'rgb(255, 255, 255) !important',
    borderColor: 'rgb(60, 64, 67) !important',
    boxShadow:
      'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.1) 0px 0px 15px 0px',
  },
  chatInputDark: {
    color: 'white !important',
    '-webkit-text-fill-color': 'rgb(64, 65, 79) !important',
    '&.MuiInputBase &.MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
      color: 'white !important',
      '-webkit-text-fill-color': 'white !important',
    },
    backgroundColor: 'rgb(64, 65, 79) !important',
    borderColor: 'rgba(32, 33, 35, 0.5) !important',
  },
  privacyNotice: {
    fontSize: '0.75rem',
    lineHeight: '1rem',
    fontFamily: 'Helvetica Neue,sans-serif',
    letterSpacing: '.1px',
    margin: '12px 24px',
    textAlign: 'center',
    color: 'rgb(55, 65, 81)',
  },
  privacyNoticeDark: {
    color: 'rgb(227, 227, 227)',
  },
});

export default ChatGPTDesktop;
